import React, { useMemo, useState } from 'react';
import { ActivityGroup } from '../types';
import { authenticatedFetch } from '../libraries/auth';
import { useAppDispatch, registerActivityGroup, unregisterActivityGroup } from '../libraries/AppContext';
import LoadingSpinner from './LoadingSpinner';
import { getFriendlyErrorMessage } from '../libraries/friendlyErrors';

const SAME_TIME_MESSAGE = 'Sie sind bereits für eine Aktivitätsgruppe zur selben Zeit angemeldet. Bitte melden Sie sich zuerst dort ab.';

interface ActivityGroupsPageProps {
  activityGroups: ActivityGroup[];
  onActivityGroupClick: (id: number) => void;
  myActivityGroupIds: number[];
  myParticipantId: number | null;
}

const ActivityGroupsPage: React.FC<ActivityGroupsPageProps> = ({ activityGroups, onActivityGroupClick, myActivityGroupIds, myParticipantId }) => {
  const dispatch = useAppDispatch();
  const [selectedType, setSelectedType] = useState<'yoga' | 'running' | 'wald_waerme'>('yoga');
  const [loadingGroupId, setLoadingGroupId] = useState<number | null>(null);
  const [errors, setErrors] = useState<{ [key: number]: string }>({});
  const [conflictGroupIds, setConflictGroupIds] = useState<{ [key: number]: number | null }>({});

  const sanitizeHTML = (html: string) => {
    if (typeof DOMParser === 'undefined') {
      return html;
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('script, style').forEach(el => el.remove());
    doc.querySelectorAll('*').forEach(el => {
      for (const attr of Array.from(el.attributes)) {
        if (attr.name.startsWith('on')) {
          el.removeAttribute(attr.name);
        }
      }
    });
    return doc.body.innerHTML;
  };

  const filteredGroups = useMemo(() => (
    activityGroups.filter(group => group.type === selectedType)
  ), [activityGroups, selectedType]);

  const registeredTimeKeys = useMemo(() => {
    return new Set(
      activityGroups
        .filter(group => myActivityGroupIds.includes(group.id))
        .map(group => {
          const date = group.agendaItem?.date || '';
          const start = group.agendaItem?.start || '';
          return `${date}|${start}`;
        })
        .filter(key => key !== '|')
    );
  }, [activityGroups, myActivityGroupIds]);

  const handleToggleRegistration = async (groupId: number) => {
    if (myParticipantId === null) {
      setErrors(prev => ({ ...prev, [groupId]: 'Teilnehmer-ID nicht gefunden' }));
      return;
    }

    setLoadingGroupId(groupId);
    setErrors(prev => ({ ...prev, [groupId]: '' }));
    setConflictGroupIds(prev => ({ ...prev, [groupId]: null }));

    try {
      const API_URL = import.meta.env.VITE_API_URL || '';
      const response = await authenticatedFetch(`${API_URL}/api/activity-groups/${groupId}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const data = await response.json();
        if (data?.error === 'already_registered_in_same_time') {
          setErrors(prev => ({ ...prev, [groupId]: SAME_TIME_MESSAGE }));
          if (typeof data?.activityGroupId === 'number') {
            setConflictGroupIds(prev => ({ ...prev, [groupId]: data.activityGroupId }));
          }
          return;
        }
        throw new Error(data?.error || 'Failed to update registration');
      }

      const isRegistered = myActivityGroupIds.includes(groupId);
      if (isRegistered) {
        unregisterActivityGroup(dispatch, groupId, myParticipantId);
      } else {
        registerActivityGroup(dispatch, groupId, myParticipantId);
      }
    } catch (e) {
      setErrors(prev => ({
        ...prev,
        [groupId]: getFriendlyErrorMessage(e, {
          genericMessage: 'Die Anmeldung konnte gerade nicht verarbeitet werden. Bitte versuchen Sie es erneut.',
          participantMessage: 'Ihr Teilnehmerprofil konnte nicht geladen werden. Bitte versuchen Sie es später erneut.',
        }),
      }));
    } finally {
      setLoadingGroupId(null);
    }
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setSelectedType('yoga')}
          className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
            selectedType === 'yoga'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-white text-gray-700 border border-gray-200'
          }`}
        >
          Yoga
        </button>
        <button
          type="button"
          onClick={() => setSelectedType('running')}
          className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
            selectedType === 'running'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-white text-gray-700 border border-gray-200'
          }`}
        >
          Jogging
        </button>
        <button
          type="button"
          onClick={() => setSelectedType('wald_waerme')}
          className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
            selectedType === 'wald_waerme'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-white text-gray-700 border border-gray-200'
          }`}
        >
          Wald & Wärme
        </button>
      </div>

      {filteredGroups.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-6 text-center text-sm text-gray-500">
          Keine Aktivitätsgruppen verfügbar.
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredGroups.map(group => {
            const agendaItem = group.agendaItem;
            const safeContent = group.content ? sanitizeHTML(group.content) : '';
            const isRegistered = myActivityGroupIds.includes(group.id);
            const timeKey = `${agendaItem?.date || ''}|${agendaItem?.start || ''}`;
            const hasTimeConflict = !isRegistered && timeKey !== '|' && registeredTimeKeys.has(timeKey);
            const isLoading = loadingGroupId === group.id;
            const error = errors[group.id];
            const conflictGroupId = conflictGroupIds[group.id];

            return (
              <div
                key={group.id}
                className="text-left bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 md:justify-between">
                      <h1 className="text-2xl font-bold text-gray-900">{group.name}</h1>
                      {isRegistered && (
                        <span className="hidden md:inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full md:ml-auto">
                          <span className="material-symbols-outlined text-base" aria-hidden="true">person</span>
                          Angemeldet
                        </span>
                      )}
                      {!isRegistered && hasTimeConflict && (
                        <span className="hidden md:inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full md:ml-auto">
                          <span className="material-symbols-outlined text-base" aria-hidden="true">error</span>
                          Zeitkonflikt
                        </span>
                      )}
                    </div>
                    <div className="mt-2 space-y-1 text-sm text-gray-500">
                      <div>
                        <span className="font-medium text-gray-700">Ort:</span>{' '}
                        <span>{agendaItem?.place || '—'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Datum:</span>{' '}
                        <span>{formatDate(agendaItem?.date || null)}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Start:</span>{' '}
                        <span>{formatTime(agendaItem?.start || null)}</span>
                      </div>
                      {agendaItem?.end && (
                        <div>
                          <span className="font-medium text-gray-700">Ende:</span>{' '}
                          <span>{formatTime(agendaItem.end)}</span>
                        </div>
                      )}
                    </div>

                    {safeContent && (
                      <div
                        className="mt-4 prose prose-sm max-w-none text-gray-700"
                        dangerouslySetInnerHTML={{ __html: safeContent }}
                      />
                    )}
                    {isRegistered && (
                      <div className="mt-3 md:hidden">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
                          <span className="material-symbols-outlined text-base" aria-hidden="true">person</span>
                          Angemeldet
                        </span>
                      </div>
                    )}
                    {!isRegistered && hasTimeConflict && (
                      <div className="mt-3 md:hidden">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                          <span className="material-symbols-outlined text-base" aria-hidden="true">error</span>
                          Zeitkonflikt
                        </span>
                      </div>
                    )}

                    {error && (
                      <div className="p-3 mt-3 text-sm text-red-800 rounded-lg bg-red-50" role="alert">
                        <div className="flex items-start gap-2">
                          <span className="material-symbols-outlined text-base flex-shrink-0" aria-hidden="true">error</span>
                          <div className="flex-1">
                            <p className="font-medium">Fehler</p>
                            <p className="text-xs mt-1">{error}</p>
                            {conflictGroupId && (
                              <button
                                type="button"
                                onClick={() => onActivityGroupClick(conflictGroupId)}
                                className="mt-2 text-red-700 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 transition text-xs font-medium"
                              >
                                Zur angemeldeten Gruppe
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => handleToggleRegistration(group.id)}
                        disabled={isLoading}
                        className={`w-full flex justify-center items-center font-medium rounded-lg text-sm px-4 py-2.5 focus:ring-4 focus:outline-none transition-colors ${
                          isRegistered
                            ? 'text-white bg-red-700 hover:bg-red-800 focus:ring-red-300 disabled:opacity-50 disabled:cursor-not-allowed'
                            : 'text-white bg-primary hover:bg-primary/90 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed'
                        }`}
                      >
                        {isLoading ? (
                          <LoadingSpinner size="sm" variant="white" />
                        ) : isRegistered ? (
                          'Abmelden'
                        ) : (
                          'Hier anmelden'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActivityGroupsPage;
